import {
  useGetReceiptBatch,
  useGetReceiptBatchPhoenix,
  useGetResidentList,
  useServiceProviderResidentList,
} from '@pflegenavi/service-provider-frontend/api';
import { useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import type {
  GetReceiptBatchResultDto,
  ReceiptBatchControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { useAuthenticationInternal } from '@pflegenavi/frontend/authentication';
import { getGlobalServiceProviderIdFromToken } from '../../../utils/getGlobalServiceProviderIdFromToken';
import { FeatureFlag, ResidentState } from '@pflegenavi/shared/api';
import type { BatchReceiptDetails } from '../form/ServiceProviderReceiptBatchForm';
import { useThrottledReceiptBatch } from '../form/utils/useThrottledReceipts';
import { v4 as uuid } from 'uuid';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';

interface UseAugmentedThrottledReceiptsProps {
  batchId: string | undefined;
}

interface UseAugmentedThrottledReceiptsResult {
  throttledReceipts: BatchReceiptDetails[];
  isThrottling: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  errorUpdateCount: number;
  isRefetching: boolean;
  batch?: GetReceiptBatchResultDto;
  totalNumberOfReceipts: number;
  totalAmount: number;
  refetchBatch: () => void;
  minReceiptDate?: Date;
  maxReceiptDate?: Date;
}

// eslint-disable-next-line complexity
export const useAugmentedThrottledReceipts = ({
  batchId,
}: UseAugmentedThrottledReceiptsProps): UseAugmentedThrottledReceiptsResult => {
  const usePhoenix = useFeatureFlagEnabled(FeatureFlag.dev_ReceiptBatchPhoenix);
  const {
    throttledReceipts,
    isThrottling,
    batch,
    isLoading,
    isError,
    error,
    errorUpdateCount,
    isRefetching,
    totalNumberOfReceipts,
    totalAmount,
    refetchBatch,
    minReceiptDate,
    maxReceiptDate,
  } = useThrottledReceiptBatch<
    ReceiptBatchControllerResultTypes['getReceiptBatch']
  >({
    batchId,
    useGetReceiptBatch: usePhoenix
      ? useGetReceiptBatchPhoenix
      : useGetReceiptBatch,
  });

  const { getTokenImmediate } = useAuthenticationInternal();

  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: residentsWithReceiptsEnabled } = useGetResidentList(
    {
      nursingHomeId: batch?.nursingHomeId ?? '',
      globalServiceProviderId: globalServiceProviderId!,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: 0,
      refetchIntervalInBackground: false,
      enabled: !!globalServiceProviderId && !!batch?.nursingHomeId,
    }
  );

  const residentsWithReceiptsEnabledById = useMemo(() => {
    return keyBy(residentsWithReceiptsEnabled ?? [], 'id');
  }, [residentsWithReceiptsEnabled]);

  const idsOfMissingResidents = useMemo(() => {
    return throttledReceipts
      .filter((receipt) => receipt.residentId)
      .map((receipt) => receipt.residentId!)
      .filter((residentId) => !residentsWithReceiptsEnabledById[residentId]);
  }, [throttledReceipts, residentsWithReceiptsEnabledById]);

  const { data: residentsWithReceiptsDisabled } =
    useServiceProviderResidentList(
      {
        tenant: batch?.tenant ?? '',
        nursingHomeId: batch?.nursingHomeId ?? '',
        residentStates: [
          ResidentState.awaitingSettlement,
          ResidentState.archived,
        ],
        residentIds: idsOfMissingResidents,
      },
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: 0,
        refetchIntervalInBackground: false,
        enabled:
          !!batch?.tenant &&
          !!batch?.nursingHomeId &&
          idsOfMissingResidents.length > 0,
      }
    );

  const residentsWithReceiptsDisabledById = useMemo(() => {
    return keyBy(residentsWithReceiptsDisabled ?? [], 'id');
  }, [residentsWithReceiptsDisabled]);

  const mappedReceipts = useMemo(() => {
    return throttledReceipts.map((receipt) => {
      if (!receipt.residentId) {
        return receipt;
      }

      let residentActive: boolean | undefined = true;
      let resident = residentsWithReceiptsEnabledById[receipt.residentId];

      if (!resident) {
        resident = residentsWithReceiptsDisabledById[receipt.residentId];
        residentActive = !resident;
      }

      return {
        ...receipt,
        residentName: receipt.residentName,
        residentActive: residentActive,
        residentGender: receipt.residentGender,
        individualDate: receipt.individualDate,
        lineItems:
          receipt.lineItems.length === 0
            ? [
                {
                  id: undefined,
                  _internalId: `new-${uuid()}`,
                  amount: undefined,
                  count: 1,
                  name: undefined,
                  taxes: undefined,
                },
              ]
            : receipt.lineItems,
      };
    });
  }, [
    throttledReceipts,
    residentsWithReceiptsEnabledById,
    residentsWithReceiptsDisabledById,
  ]);

  return {
    batch,
    totalNumberOfReceipts,
    totalAmount,
    isLoading,
    isError,
    error,
    errorUpdateCount,
    isRefetching,
    isThrottling,
    throttledReceipts: mappedReceipts,
    refetchBatch,
    minReceiptDate,
    maxReceiptDate,
  };
};
